import { inkService } from './ink.service';
export const knowledgeService = {
    /**
     * Fetch all knowledge articles for organization (now uses ink table)
     */
    async fetchArticles(organizationId, limit = 50) {
        try {
            const articles = await inkService.getArticles(organizationId);
            // Apply limit and map to KnowledgeArticle format
            return articles
                .slice(0, limit)
                .map(doc => this.mapInkToArticle(doc));
        }
        catch (error) {
            console.error('Failed to fetch articles:', error);
            return [];
        }
    },
    /**
     * Fetch articles by category
     */
    async fetchArticlesByCategory(organizationId, category, limit = 50) {
        try {
            const articles = await inkService.getArticles(organizationId, category);
            return articles
                .slice(0, limit)
                .map(doc => this.mapInkToArticle(doc));
        }
        catch (error) {
            console.error('Failed to fetch articles by category:', error);
            return [];
        }
    },
    /**
     * Search articles
     */
    async searchArticles(organizationId, searchQuery, limit = 50) {
        try {
            const articles = await inkService.search(organizationId, searchQuery, 'article');
            return articles
                .slice(0, limit)
                .map(doc => this.mapInkToArticle(doc));
        }
        catch (error) {
            console.error('Failed to search articles:', error);
            return [];
        }
    },
    /**
     * Get single article by ID
     */
    async getArticle(id) {
        try {
            const article = await inkService.getById(id);
            if (!article)
                return null;
            return this.mapInkToArticle(article);
        }
        catch (error) {
            console.error('Failed to fetch article:', error);
            return null;
        }
    },
    /**
     * Create new article (now uses unified ink table)
     */
    async createArticle(data) {
        try {
            const article = await inkService.create({
                title: data.title,
                content: data.content || '',
                document_type: 'article',
                category: data.category,
                description: data.description || '',
                organization_id: data.organization_id,
                owner_id: data.author_id,
                created_by: data.author_id,
                status: 'published',
                is_published: true,
                source_type: 'manual_write',
                processing_status: 'processed'
            });
            if (!article)
                return null;
            // Map ink document to knowledge article format for backward compatibility
            return this.mapInkToArticle(article);
        }
        catch (error) {
            console.error('Error creating article:', error);
            return null;
        }
    },
    /**
     * Map ink document to knowledge article format
     */
    mapInkToArticle(doc) {
        return {
            id: doc.id,
            title: doc.title,
            category: doc.category || '',
            content: doc.content || '',
            description: doc.description || '',
            views: doc.views,
            author_id: doc.owner_id,
            organization_id: doc.organization_id,
            created_at: doc.created_at,
            updated_at: doc.updated_at,
            last_updated: new Date(doc.updated_at).toLocaleDateString()
        };
    },
    /**
     * Update article
     */
    async updateArticle(id, data) {
        try {
            const article = await inkService.update(id, {
                title: data.title,
                content: data.content,
                category: data.category,
                description: data.description
            });
            if (!article)
                return null;
            return this.mapInkToArticle(article);
        }
        catch (error) {
            console.error('Error updating article:', error);
            return null;
        }
    },
    /**
     * Delete article
     */
    async deleteArticle(id) {
        return inkService.delete(id);
    },
    /**
     * Increment view count for article
     */
    async incrementViewCount(id) {
        return inkService.incrementViews(id);
    },
    /**
     * Get distinct categories for organization
     */
    async getCategories(organizationId) {
        try {
            const categories = await inkService.getCategories(organizationId, 'article');
            return categories.map(name => ({
                name,
                color: this.getCategoryColor(name)
            }));
        }
        catch (error) {
            console.error('Failed to fetch categories:', error);
            return [];
        }
    },
    /**
     * Get color for category (helper)
     */
    getCategoryColor(category) {
        const colors = {
            'Guides': '#3B82F6',
            'Best Practices': '#10B981',
            'Technical': '#8B5CF6',
            'FAQs': '#F59E0B',
            'Getting Started': '#EC4899',
            'Tutorials': '#06B6D4',
            'API': '#F97316',
            'Administration': '#DC2626'
        };
        return colors[category] || '#6B7280';
    }
};
